<template>
  <v-row>
    <v-col cols="12">
      <v-card class="card-container" elevation="0">
        <v-data-table
          :headers="headers"
          :items="pagedItems"
          :item-key="'_id'"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          :height="tableHeight"
          fixed-header
          hide-default-footer
        >
          <template v-slot:[`item.riderName`]="{ item }">
            {{ formatRiderName(item) }}
          </template>

          <template v-slot:[`item.email`]="{ item }">
            {{ formatEmail(item) }}
          </template>

          <template v-slot:[`item.phone`]="{ item }">
            {{ formatPhone(item) }}
          </template>

          <template v-slot:[`item.vehicleType`]="{ item }">
            {{ formatVehicleType(item) }}
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              small
              :color="getStatusDisplay(item).color"
              :text-color="getStatusDisplay(item).text"
              class="status-chip"
            >
              <v-icon v-if="getStatusDisplay(item).icon" left small>
                {{ getStatusDisplay(item).icon }}
              </v-icon>
              {{ getStatusDisplay(item).label }}
            </v-chip>
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center">
              <v-btn
                color="#1F2A39"
                text
                class="d-flex align-center action-btn"
                @click="$emit('view', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#F4811F">mdi-eye-outline</v-icon>
                </div>
                <span>รายละเอียด</span>
              </v-btn>

              <template v-if="normalizeStatus(item.status) === 'pending_approval' && canSuspend">
                <v-btn
                  color="#198C19"
                  text
                  class="d-flex align-center action-btn ml-2"
                  @click="$emit('approve', item)"
                >
                  <div class="detail-btn mr-2">
                    <v-icon size="20" color="#198C19">mdi-check-circle-outline</v-icon>
                  </div>
                  <span>อนุมัติ</span>
                </v-btn>

                <v-btn
                  color="#EE2526"
                  text
                  class="d-flex align-center action-btn ml-2"
                  @click="$emit('reject', item)"
                >
                  <div class="detail-btn mr-2">
                    <v-icon size="20" color="#EE2526">mdi-close-circle-outline</v-icon>
                  </div>
                  <span>ปฏิเสธ</span>
                </v-btn>
              </template>

              <v-btn
                v-if="canSuspend && normalizeStatus(item.status) === 'active'"
                color="#1F2A39"
                text
                class="d-flex align-center action-btn ml-2"
                @click="$emit('toggle-suspend', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#EE2526">mdi-block-helper</v-icon>
                </div>
                <span>ระงับบัญชี</span>
              </v-btn>

              <v-btn
                v-if="canSuspend && normalizeStatus(item.status) === 'suspended'"
                color="#1F2A39"
                text
                class="d-flex align-center action-btn ml-2"
                @click="$emit('toggle-suspend', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#198C19">mdi-lock-open-outline</v-icon>
                </div>
                <span>ยกเลิกระงับ</span>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <v-row class="mt-4" v-if="riders.length > 10">
        <v-col
          cols="12"
          md="6"
          class="d-flex align-center justify-center justify-md-start"
        >
          <span class="count-text mr-2">แสดง</span>

          <v-menu v-model="menu" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <div
                class="custom-select"
                v-bind="attrs"
                v-on="on"
                :class="{ 'menu-open': menu }"
              >
                <span class="items-per-page-text">
                  {{ currentItemsPerPageText }}
                </span>
                <img src="@/assets/caret_down.png" class="caret-icon ml-1" />
              </div>
            </template>

            <v-list>
              <v-list-item
                v-for="option in itemsPerPageOptions"
                :key="option.value"
                @click="selectOption(option)"
              >
                <v-list-item-title>{{ option.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <span class="count-text ml-2">
            จากทั้งหมด {{ riders.length }} รายการ
          </span>
        </v-col>

        <v-col
          cols="12"
          md="6"
          class="d-flex align-center justify-center justify-md-end pa-0"
        >
          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="7"
            prev-icon="ก่อนหน้า"
            next-icon="ถัดไป"
            class="mx-2"
            color="#F4811F"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "Table",

  props: {
    riders: {
      type: Array,
      required: true,
    },
    userRole: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      page: 1,
      menu: false,
      itemsPerPage: 10,
      itemsPerPageOptions: [
        { text: "10", value: 10 },
        { text: "100", value: 100 },
        { text: "ทั้งหมด", value: -1 },
      ],
    };
  },

  computed: {
    pageCount() {
      if (this.itemsPerPage === -1) return 1;
      return Math.ceil(this.riders.length / this.itemsPerPage);
    },

    pagedItems() {
      if (this.itemsPerPage === -1) return this.riders;
      const start = (this.page - 1) * this.itemsPerPage;
      return this.riders.slice(start, start + this.itemsPerPage);
    },

    tableHeight() {
      return this.pagedItems.length < 7 ? "auto" : "380px";
    },

    currentItemsPerPageText() {
      const opt = this.itemsPerPageOptions.find(
        (x) => x.value === this.itemsPerPage
      );
      return opt ? opt.text : this.itemsPerPage;
    },

    normalizedUserRole() {
      return (this.userRole || "").toString().trim().toLowerCase();
    },

    canSuspend() {
      return ["admin", "superadmin"].includes(this.normalizedUserRole);
    },

    headers() {
      return [
        {
          text: "ชื่อพนักงานส่ง",
          value: "riderName",
          align: "left",
          class: "header-text",
          cellClass: "cell-normal",
          sortable: false,
          width: "240px",
        },
        {
          text: "อีเมล",
          value: "email",
          align: "left",
          class: "header-text",
          cellClass: "cell-normal",
          sortable: false,
          width: "260px",
        },
        {
          text: "เบอร์โทรศัพท์",
          value: "phone",
          align: "left",
          class: "header-text",
          cellClass: "cell-normal",
          sortable: false,
          width: "180px",
        },
        {
          text: "ประเภทรถ",
          value: "vehicleType",
          align: "left",
          class: "header-text",
          cellClass: "cell-normal",
          sortable: false,
          width: "180px",
        },
        {
          text: "สถานะ",
          value: "status",
          align: "left",
          class: "header-text",
          cellClass: "cell-normal",
          sortable: false,
          width: "200px",
        },
        {
          text: "วันที่สมัคร",
          value: "createdAt",
          align: "left",
          class: "header-text",
          cellClass: "cell-normal",
          sortable: false,
          width: "220px",
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "left",
          class: "header-actions",
          cellClass: "cell-actions",
          sortable: false,
          width: "200px",
        },
      ];
    },
  },

  watch: {
    page() {
      this.scrollToTopOfTable();
    },
    riders() {
      this.page = 1;
      this.scrollToTopOfTable();
    },
  },

  methods: {
    formatRiderName(item) {
      const user = item.user || {};
      const personal = item.personalInfo || {};

      const prefix = (personal.prefix || user.prefix || "").trim();
      const first = (personal.firstName || user.firstName || "").trim();
      const last = (personal.lastName || user.lastName || "").trim();

      const parts = [prefix, first, last].filter(Boolean);

      if (parts.length) {
        return parts.join(" ");
      }

      return user.username || "-";
    },

    formatEmail(item) {
      const user = item.user || {};
      return (
        user.email ||
        item.email || "-"
      );
    },

    formatPhone(item) {
      return (
        item.phone ||
        item.phoneNumber ||
        item.phone_number ||
        "-"
      );
    },

    formatVehicleType(item) {
      if (!item.vehicles || !item.vehicles.length) return "-";
      const v = item.vehicles[0];
      return v.vehicleType?.name || v.vehicleType || "-";
    },

    formatDate(d) {
      if (!d) return "-";

      const date = new Date(d);
      if (isNaN(date.getTime())) return "-";

      const monthsFullThai = [
        "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
        "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
      ];

      const day = date.getDate();
      const month = monthsFullThai[date.getMonth()];
      const year = date.getFullYear() + 543;

      return `${day} ${month} ${year}`;
    },

    normalizeStatus(status) {
      const v = (status || "").toString().trim().toLowerCase();
      if (v === "pending_approval") return "pending_approval";
      if (v === "active") return "active";
      if (v === "inactive") return "inactive";
      if (v === "rejected") return "rejected";
      if (["suspended", "ban", "banned"].includes(v)) return "suspended";
      return "unknown";
    },

    getStatusDisplay(item) {
      const s = this.normalizeStatus(item.status);

      const map = {
        pending_approval: { label: "รอการอนุมัติ", color: "#FEF3C7", text: "#D97706", icon: "mdi-clock-outline" },
        active:           { label: "ใช้งานปกติ", color: "#E1FAE8", text: "#0FAE63", icon: "mdi-check-circle-outline" },
        inactive:         { label: "ปิดการใช้งาน", color: "#F3F4F6", text: "#6B717F", icon: "mdi-pause-circle-outline" },
        rejected:         { label: "ถูกปฏิเสธ", color: "#FEE2E2", text: "#DC2626", icon: "mdi-close-circle-outline" },
        suspended:        { label: "ถูกระงับ", color: "#FDECEC", text: "#E11D48", icon: "mdi-block-helper" },
      };

      return map[s] || { label: "ไม่ทราบสถานะ", color: "#E5E7EB", text: "#4B5563", icon: "mdi-help-circle-outline" };
    },

    selectOption(option) {
      this.itemsPerPage = option.value;
      this.page = 1;
      this.menu = false;
    },

    scrollToTopOfTable() {
      this.$nextTick(() => {
        const wrapper = this.$el.querySelector(".v-data-table__wrapper");
        if (wrapper) {
          wrapper.scrollTo({ top: 0 });
        } else {
          window.scrollTo({ top: 0 });
        }
      });
    },
  },
};
</script>

<style scoped>
.card-container {
  overflow: hidden !important;
  border-radius: 12px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
}
::v-deep(.cell-normal),
::v-deep(.cell-actions) {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #1f2a39 !important;
}
::v-deep(.header-text),
::v-deep(.header-actions) {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #6b717f !important;
  background-color: #f9fafc !important;
  box-shadow: none !important;
}
.status-chip {
  border-radius: 8px !important;
}
.count-text {
  font-size: 14px;
  font-weight: 400;
  color: #394253;
}
.items-per-page-text {
  font-size: 14px;
  font-weight: 400;
  color: #394253;
}
.custom-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
}
.caret-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}
.custom-select.menu-open .caret-icon {
  transform: rotate(180deg);
}
::v-deep(.cell-actions) {
  position: sticky;
  right: 0;
  background: #fff;
  z-index: 2;
}
::v-deep(.header-actions) {
  position: sticky;
  right: 0;
  background: #f9fafc;
  box-shadow: -2px 0 3px -1px #1018280f !important;
  z-index: 3;
}
::v-deep .v-pagination__item,
::v-deep .v-pagination__navigation {
  font-size: 14px !important;
  font-weight: 400 !important;
  border-radius: 8px !important;
  width: 34px !important;
  height: 34px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
}
::v-deep(.v-pagination .v-pagination__navigation .v-icon) {
  font-size: 12px !important;
  font-weight: 400 !important;
  color: #6b717f !important;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-style: normal !important;
}
::v-deep(.v-pagination .v-pagination__navigation) {
  width: 65px !important;
  height: 34px !important;
}
.action-btn {
  min-width: 0 !important;
  padding: 0 4px !important;
}
.detail-btn {
  border-radius: 4px !important;
  padding: 2px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
  width: 24px !important;
  height: 24px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
